<template>
  <article class="dd-post">
    <!-- Media area: placeholder (locked) or embed (open) -->
    <div class="dd-post__media" v-if="showMedia">
      <template v-if="hasImage">
        <div class="dd-post__media-img-wrap">
          <img class="dd-post__img--cover" :src="imageDisplayUrl" alt="post image" />
        </div>
        <div v-if="showImageLock" class="dd-post__lock">{{ $t('wall.post.locked') }}</div>
      </template>
      <template v-else>
        <template v-if="isLocked">
          <template v-if="embedFromPublic">
            <div class="dd-post__embed">
              <iframe
                v-if="embedKind==='youtube'"
                class="dd-post__iframe"
                :src="embedURL"
                title="YouTube player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
              <iframe
                v-else-if="embedKind==='twitch'"
                class="dd-post__iframe"
                :src="embedURL"
                title="Twitch player"
                frameborder="0"
                allowfullscreen
              ></iframe>
            </div>
          </template>
          <template v-else>
            <div class="dd-post__media-img-wrap">
              <img class="dd-post__img--cover" :src="coverImage" alt="locked cover" />
            </div>
            <div class="dd-post__lock">{{ $t('wall.post.locked') }}</div>
          </template>
        </template>
        <template v-else>
          <div v-if="embedURL" class="dd-post__embed">
            <iframe
              v-if="embedKind==='youtube'"
              class="dd-post__iframe"
              :src="embedURL"
              title="YouTube player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            ></iframe>
            <iframe
              v-else-if="embedKind==='twitch'"
              class="dd-post__iframe"
              :src="embedURL"
              title="Twitch player"
              frameborder="0"
              allowfullscreen
            ></iframe>
          </div>
        </template>
      </template>
    </div>

    <div class="dd-post__body">
      <!-- 1. Title is always present -->
      <h3 class="dd-post__title">{{ post.title }}</h3>

      <!-- 3. Public text with expand/collapse -->
      <div v-if="publicText" ref="publicTextEl" class="dd-post__text" :class="{ 'dd-post__text--clamped': !expandedText }">
        {{ publicText }}
      </div>
      <button v-if="publicText && (publicOverflow || expandedText)" class="dd-post__more" @click="togglePublic()">
        <span>{{ expandedText ? $t('hide_all') : $t('show_all') }}</span>
      </button>

      <!-- 4. Secret text block -->
      <div v-if="isLocked && hasHiddenText" class="dd-post__linkbox dd-post__linkbox--between">
        <span class="dd-post__linkicon"><img :src="icons.text" alt="text" width="16" height="16"/></span>
        <span class="dd-post__muted dd-post__mid">{{ $t('wall.post.text_only_for_sponsors') }}</span>
        <span class="dd-post__linktail"><img :src="icons.lock" alt="private" width="16" height="16"/></span>
      </div>
      <div v-else-if="hasHiddenText" class="dd-post__secret">
        <div class="dd-post__secret-head">
          <img :src="icons.unlock" alt="unlock" width="16" height="16"/>
          <h4 class="dd-post__secret-title">{{ $t('wall.post.sponsors_only_text_title') }}</h4>
        </div>
        <div ref="secretTextEl" class="dd-post__secret-body" :class="{ 'dd-post__text--clamped': !expandedSecret }">{{ hiddenText }}</div>
        <div class="dd-post__secret-more">
          <button v-if="secretOverflow || expandedSecret" class="dd-post__more" @click="toggleSecret()">{{ expandedSecret ? $t('hide_all') : $t('show_all') }}</button>
        </div>
      </div>

      <!-- 5. Public link block -->
      <div v-if="publicLinkVisible && !embedFromPublic" class="dd-post__linkbox dd-post__linkbox--between">
        <span class="dd-post__linkicon">
          <img :src="leftIcon(kindPublic)" alt="kind" width="16" height="16"/>
        </span>
        <a class="dd-post__link" :href="publicURL" target="_blank" rel="noopener nofollow">{{ publicHost }}</a>
        <span class="dd-post__linktail"><img :src="icons.people" alt="public" width="16" height="16"/></span>
      </div>

      <!-- 6. Secret link block -->
      <div v-if="(hiddenURL || post.attachmentURLRemoved) && isLocked" class="dd-post__linkbox dd-post__linkbox--between">
        <span class="dd-post__linkicon">
          <img :src="leftIcon(kindHidden)" alt="kind" width="16" height="16"/>
        </span>
        <span class="dd-post__muted">{{ $t('wall.post.sponsors_only_link_locked') }}</span>
        <span class="dd-post__linktail"><img :src="icons.lock" alt="private" width="16" height="16"/></span>
      </div>
      <div v-else-if="hiddenURL && !isLocked" class="dd-post__linkbox dd-post__linkbox--between">
        <span class="dd-post__linkicon">
          <img :src="leftIcon(kindHidden)" alt="kind" width="16" height="16"/>
        </span>
        <a class="dd-post__link" :href="hiddenURL" target="_blank" rel="noopener nofollow">{{ hiddenHost }}</a>
        <span class="dd-post__linktail"><img :src="icons.unlock" alt="unlocked" width="16" height="16"/></span>
      </div>

      <!-- 6. Locked posts: show accessible levels and allow to expand list -->
      <div v-if="isLocked" class="dd-post__access">
        <span v-if="accessibleLevels.length === 1">Доступно на уровне {{ accessibleLevels[0].name }}</span>
        <span v-else>Доступно на {{ accessibleLevels.length }} уровнях спонсорства</span>
        <button v-if="accessibleLevels.length > 1" class="dd-post__levels-btn" @click="levelsOpen = !levelsOpen">
          {{ levelsOpen ? 'Скрыть уровни' : 'Показать уровни' }}
        </button>
        <ul v-if="levelsOpen && accessibleLevels.length > 1" class="dd-post__levels">
          <li v-for="lvl in accessibleLevels" :key="lvl.externalID">{{ lvl.name }}</li>
        </ul>
        <div class="dd-post__cta">
          <button class="dd-post__btn" @click="goSubscribe">{{ $t('wall.post.unlock') }}</button>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import {logWarn} from '@/helpers';
export default {
  name: 'PostCard',
  props: {
    post: { type: Object, required: true },
  },
  data() {
    return {
      expandedText: false,
      expandedSecret: false,
      levelsOpen: false,
      publicOverflow: false,
      secretOverflow: false,
    };
  },
  computed: {
    isLocked() { return !!this.post.locked; },
    publicText() { return (this.post.publicText || '').trim(); },
    publicClamped() { return this.publicText.length > 180; },
    hiddenText() { return (this.post.hiddenText || '').trim(); },
    hasHiddenText() {
      return !!this.hiddenText
        || this.post.hiddenTextRemoved === true
        || this.post.hidden_text_removed === true;
    },
    hiddenURL() { return this.post.attachmentUrl || null; },
    publicURL() { return this.post.publicAttachment || null; },
    imageURL() { return this.post.imageUrl || null; },
    imageLocked() { return !!this.post.imageRemoved; },
    hasImage() { return !!this.imageURL || this.imageLocked; },
    imageDisplayUrl() { return this.imageURL || this.imagePlaceholder; },
    kindHidden() { return this.kindOf(this.hiddenURL, this.post.attachmentURLKind); },
    kindPublic() { return this.kindOf(this.publicURL, null); },
    // Select which URL to embed: hidden when unlocked, public when locked
    embedSourceURL() { return this.isLocked ? this.publicURL : this.hiddenURL; },
    embedKind() { return this.isLocked ? this.kindPublic : this.kindHidden; },
    embedFromPublic() { return this.isLocked && (this.embedKind === 'youtube' || this.embedKind === 'twitch') && !!this.embedSourceURL; },
    embedURL() {
      const urlStr = this.embedSourceURL;
      const kind = this.embedKind;
      if (!urlStr) return '';
      try {
        if (kind === 'youtube') {
          const id = this.youtubeIdFromURL(urlStr);
          return id ? `https://www.youtube.com/embed/${id}?autoplay=0` : '';
        }
        if (kind === 'twitch') {
          const url = new URL(urlStr);
          const parent = window.location.hostname;
          const host = url.hostname.toLowerCase();
          const path = url.pathname;
          // Clips: clips.twitch.tv/<slug> OR /<channel>/clip/<slug>
          if (host.startsWith('clips.twitch.tv') || /\/clip\//.test(path)) {
            let slug = '';
            if (host.startsWith('clips.twitch.tv')) {
              slug = path.replace(/^\//, '').split('/')[0] || '';
            } else {
              const cm = path.match(/\/clip\/([A-Za-z0-9_-]+)/);
              if (cm && cm[1]) slug = cm[1];
            }
            return slug ? `https://clips.twitch.tv/embed?clip=${slug}&parent=${parent}&autoplay=false` : '';
          }
          // VOD: /videos/<id> or /video/<id>
          const vm = path.match(/\/(?:videos|video)\/(\d+)/);
          if (vm && vm[1]) {
            const vid = vm[1].startsWith('v') ? vm[1] : `v${vm[1]}`;
            return `https://player.twitch.tv/?video=${vid}&parent=${parent}&autoplay=false`;
          }
          // Live channel: /<channel>
          const seg = path.replace(/^\//, '').split('/')[0] || '';
          if (seg && seg !== 'directory' && seg !== 'p') {
            return `https://player.twitch.tv/?channel=${encodeURIComponent(seg)}&parent=${parent}&autoplay=false`;
          }
          return '';
        }
      } catch (e) {
        logWarn('Failed to build embed URL for wall post attachment', e);
      }
      return '';
    },
    showMedia() {
      if (this.hasImage) return true;
      if (this.isLocked) {
        // if we can embed from public link, do it; otherwise show cover
        return !!this.embedURL || !!this.coverImage;
      }
      return !!this.embedURL;
    },
    hiddenLinkVisible() { return (!!this.hiddenURL || this.post.attachmentURLRemoved === true || this.post.attachment_url_removed === true) && !this.isLocked; },
    publicLinkVisible() { return !!this.publicURL; },
    hiddenHost() {
      try {
        return new URL(this.hiddenURL).hostname;
      } catch (e) {
        if (this.hiddenURL) {
          logWarn('Failed to parse hidden attachment URL for wall post', e);
        }
        return this.hiddenURL;
      }
    },
    publicHost() {
      try {
        return new URL(this.publicURL).hostname;
      } catch (e) {
        if (this.publicURL) {
          logWarn('Failed to parse public attachment URL for wall post', e);
        }
        return this.publicURL;
      }
    },
    // Levels available for this post (by internal IDs)
    accessibleLevels() {
      const levels = this.$store.getters.sponsorshipLevels || [];
      const byID = Object.create(null);
      levels.forEach(l => { byID[String(l.ID)] = l; });
      const ids = (this.post.levelIDs || this.post.Levels || []).map(x => String(x));
      const out = [];
      ids.forEach(i => { const lvl = byID[i]; if (lvl && !out.includes(lvl)) out.push(lvl); });
      return out;
    },
    // Placeholder cover for locked content
    coverImage() {
      const hiddenText = !!this.post.hiddenTextRemoved;
      const hiddenLink = !!this.post.attachmentURLRemoved;
      const kind = this.kindHidden || 'link';
      const pick = (base, count = 4) => this.pickCover(base, count);
      if (hiddenLink && hiddenText) {
        if (kind === 'youtube') return pick('text-youtube', 4);
        if (kind === 'twitch') return pick('text-twitch', 4);
        if (kind === 'telegram') return pick('text-link', 3); // no specific telegram+text set, use link
        return pick('text-link', 3);
      }
      if (hiddenLink) {
        if (kind === 'youtube') return pick('youtube', 4);
        if (kind === 'twitch') return pick('twitch', 4);
        if (kind === 'telegram') return pick('telegram', 4);
        return pick('link', 3);
      }
      if (hiddenText) {
        return pick('text', 4);
      }
      return pick('content-without-hidden-media', 4);
    },
    imagePlaceholder() {
      return this.pickCover('content-without-hidden-media', 4);
    },
    showImageLock() {
      return this.isLocked && this.imageLocked;
    },
    icons() {
      return {
        youtube: require('@/assets/img/youtube.svg'),
        twitch: require('@/assets/img/twitch.svg'),
        telegram: require('@/assets/img/telegram.svg'),
        eye: require('@/assets/img/eye.svg'),
        globe: require('@/assets/img/globe.svg'),
        people: require('@/assets/img/people.svg'),
        unlock: require('@/assets/img/unlock.svg'),
        lock: require('@/assets/img/lock.svg'),
        text: require('@/assets/img/text.svg'),
      };
    },
  },
  methods: {
    togglePublic() {
      this.expandedText = !this.expandedText;
      if (!this.expandedText) this.$nextTick(this.measureOverflows);
    },
    toggleSecret() {
      this.expandedSecret = !this.expandedSecret;
      if (!this.expandedSecret) this.$nextTick(this.measureOverflows);
    },
    kindOf(url, providedKind) {
      const k = (providedKind || '').toLowerCase();
      if (k) return k;
      const u = (url || '').toLowerCase();
      if (!u) return '';
      if (u.includes('youtu')) return 'youtube';
      if (u.includes('twitch.tv')) return 'twitch';
      if (u.includes('t.me') || u.includes('telegram')) return 'telegram';
      return 'link';
    },
    youtubeIdFromURL(u) {
      try {
        const url = new URL(u);
        // short form youtu.be/<id>
        if (url.hostname.includes('youtu.be')) {
          return url.pathname.replace(/^\//, '').split('/')[0] || '';
        }
        // standard watch URL ?v=<id>
        const v = url.searchParams.get('v');
        if (v) return v;
        // live URL /live/<id>
        const liveMatch = url.pathname.match(/\/live\/([A-Za-z0-9_-]+)/);
        if (liveMatch && liveMatch[1]) return liveMatch[1];
        // shorts URL /shorts/<id>
        const shorts = url.pathname.match(/\/shorts\/([A-Za-z0-9_-]+)/);
        if (shorts && shorts[1]) return shorts[1];
        // embed/<id>
        const embed = url.pathname.match(/\/embed\/([A-Za-z0-9_-]+)/);
        if (embed && embed[1]) return embed[1];
      } catch (e) {
        if (u) {
          logWarn('Failed to parse YouTube URL for wall post', e);
        }
      }
      return '';
    },
    leftIcon(kind) {
      if (kind === 'youtube') return this.icons.youtube;
      if (kind === 'twitch') return this.icons.twitch;
      if (kind === 'telegram') return this.icons.telegram;
      return this.icons.globe;
    },
    goSubscribe() {
      this.$router.push('/wall/subscription');
    },
    rand(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    pickCover(base, count = 4) {
      return `/media-covers/${base}-${this.rand(1, count)}.png`;
    },
    measureOverflows() {
      const check = (el) => {
        if (!el) return false;
        try {
          return el.scrollHeight > el.clientHeight + 1;
        } catch (e) {
          logWarn('Failed to measure text overflow for wall post', e);
          return false;
        }
      };
      this.publicOverflow = check(this.$refs.publicTextEl);
      this.secretOverflow = check(this.$refs.secretTextEl);
    },
  },
  mounted() {
    // Initialize expanded state based on backend flag
    if (this.post && this.post.expandedByDefault) {
      this.expandedText = true;
      this.expandedSecret = true;
    }
    this.$nextTick(this.measureOverflows);
    this._resizeHandler = () => this.measureOverflows();
    window.addEventListener('resize', this._resizeHandler);
  },
  beforeDestroy() {
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },
}
</script>

<style scoped>
.dd-post__embed { position: relative; padding-top: 56.25%; }
.dd-post__iframe { position: absolute; left: 0; top: 0; width: 100%; height: 100%; }
.dd-post__media-img-wrap { position: relative; width: 100%; padding-top: 56.25%; overflow: hidden; background: #000; }
.dd-post__img--cover { position: absolute; left: 0; top: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
.dd-post__text--clamped { display: -webkit-box; -webkit-line-clamp: 5; -webkit-box-orient: vertical; overflow: hidden; }
.dd-post__more {
  background: transparent;
  border: none;
  color: var(--dd-accent, #ff6a00);
  cursor: pointer;
  padding: 0;
  display: inline;
  text-decoration: underline;
  align-self: flex-end;
  margin-top: auto;
}
.dd-post__linkbox { display: flex; align-items: center; gap: 8px; border: 1px dashed var(--border-color, #ccc); border-radius: 8px; padding: 8px 10px; margin-top: 6px; margin-bottom: 6px; }
.dd-post__linkbox--between { justify-content: space-between; }
.dd-post__link { color: var(--accent-color, #ff6a00); text-decoration: none; }
.dd-post__linkicon img { display: inline-block; vertical-align: middle; }
.dd-post__linktail img { display: inline-block; vertical-align: middle; }
.dd-post__muted { color: var(--dd-text-muted); }
.dd-post__mid { flex: 1; text-align: center; }
.dd-post__levels { margin: 8px 0 0; padding-left: 18px; }
.dd-post__levels-btn { margin-left: 8px; background: transparent; border: none; color: var(--accent-color, #ff6a00); cursor: pointer; }

/* Secret text block */
.dd-post__secret { border: 1px dashed var(--border-color, #ccc); border-radius: 8px; margin-top: 10px; overflow: hidden; }
.dd-post__secret-head { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-bottom: 1px solid var(--border-color, #ddd); background: rgba(0,0,0,0.03); }
.dd-post__secret-title { margin: 0; font-size: 16px; }
.dd-post__secret-body { padding: 10px; white-space: pre-wrap; color: var(--dd-text); }
.dd-post__secret-body { position: relative; }
.dd-post__secret-body.dd-post__text--clamped::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 10px;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0, var(--dd-card-bg, #fff) 4px, var(--dd-card-bg, #fff) 100%);
  pointer-events: none;
}
.dd-post__secret-more { display: flex; justify-content: flex-end; padding: 4px 10px 10px; }
</style>
